import { PageContainer, Spinner, AppButton, Flex } from "@/components/common";
import { Alert, App, Input, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { fetchAltCulturalCapabilities, fetchCulturalCapabilities, submitCulturalCapabilities } from "@/services/survey.service";
import { useQuery, useMutation } from "@tanstack/react-query";
import React, { useState, useMemo } from "react";
import { envConfig, SurveyApiType } from "@/configs";
import SurveysList from "../SurveysList";

const { TextArea } = Input;

const createDefaultCulturalCapabilities = (): CulturalCapabilitiesEntity => ({
  quranNahjHafez: false,
  quranNahjGhari: false,
  quranNahjDesc: '',

  madahiMoludi: false,
  madahiAdyie: false,
  madahiRoze: false,
  madahiDesc: '',

  khatati: false,
  khatatiType: '',
  khatatiLevel: '',

  sanayeDasti: false,
  sanayeDastiDesc: '',

  naghashi: false,
  naghashiTools: '',
  naghashiLevel: '',

  akasi: false,
  akasiDurbin: '',

  music: false,
  musicTools: '',
  musicDesc: '',

  karikator: false,
  karikatorDesc: '',

  dastan: false,
  dastanMethod: '',

  sher: false,
  sherDesc: '',

  shaer: false,
  shaerDesc: '',

  moshaere: false,
  moshaereDesc: '',

  nashrieElmi: false,
  nashrieSiasi: false,
  nashrieEjtemayi: false,
  nashrieMazhabi: false,
  nashrieDesc: '',

  maghale: false,
  maghaleDesc: '',

  weblog: false,
  weblogAddress: '',
  weblogDesc: '',

  varzeshDesc: '',
  varzeshTeam: '',
  kasbeMagham: '',

  khabgahTarbiatBadani: false,
  khabgahKetabkhane: false,
  khabgahTaghzie: false,
  khabgahTablighat: false,
  khabgahTasisat: false,
  khabgahModiriat: false,
  khabgahOrduyi: false,
  khabgahSite: false,

  ruznameNames: '',
  filmNames: '',
  bookNames: '',
  bookTheme: '',

  anjomanEslami: false,
  basij: false,
  mazhabi: false,
  farhangsara: false,
  masjed: false,
  shoraMadrese: false,
  basijMahale: false,
  moaseseFarhangi: false,
  ngo: false,
  fogheBarnameDesc: ''
});

const mergeWithDefaults = (existingData?: CulturalCapabilitiesEntity): CulturalCapabilitiesEntity => {
  const defaults = createDefaultCulturalCapabilities();
  return { ...defaults, ...existingData };
};

const buildSubmissionData = (
  selectedCapabilities: string[],
  formData: Record<string, string>,
  capabilityMapping: typeof CAPABILITY_MAPPING,
  existingData?: CulturalCapabilitiesEntity | any
): CulturalCapabilitiesEntity => {
  const submissionData = existingData ? mergeWithDefaults(existingData) : createDefaultCulturalCapabilities();

  selectedCapabilities.forEach(capabilityKey => {
    const config = capabilityMapping[capabilityKey];
    if (config.fields) {
      config.fields.forEach(field => {
        (submissionData as any)[field] = true;
      });
    }
  });

  Object.entries(formData).forEach(([key, value]) => {
    if (value.trim()) {
      (submissionData as any)[key] = value;
    }
  });

  return submissionData;
};

const CAPABILITY_MAPPING: Record<string, {
  label: string;
  fields?: string[];
  inputFields?: Array<{
    key: string;
    label: string;
  }>;
}> = {
  quranNahj: {
    label: "قرآن و نهج‌البلاغه",
    fields: ["quranNahjHafez", "quranNahjGhari"],
    inputFields: [
      { key: "quranNahjDesc", label: "توضیحات قرآن و نهج‌البلاغه" }
    ]
  },
  madahi: {
    label: "مداحی",
    fields: ["madahiMoludi", "madahiAdyie", "madahiRoze"],
    inputFields: [
      { key: "madahiDesc", label: "توضیحات مداحی" }
    ]
  },
  khatati: {
    label: "خطاطی",
    fields: ["khatati"],
    inputFields: [
      { key: "khatatiType", label: "نوع خطاطی" },
      { key: "khatatiLevel", label: "سطح خطاطی" }
    ]
  },
  sanayeDasti: {
    label: "صنایع دستی",
    fields: ["sanayeDasti"],
    inputFields: [
      { key: "sanayeDastiDesc", label: "توضیحات صنایع دستی" }
    ]
  },
  naghashi: {
    label: "نقاشی",
    fields: ["naghashi"],
    inputFields: [
      { key: "naghashiTools", label: "ابزارهای نقاشی" },
      { key: "naghashiLevel", label: "سطح نقاشی" }
    ]
  },
  akasi: {
    label: "عکاسی",
    fields: ["akasi"],
    inputFields: [
      { key: "akasiDurbin", label: "نوع دوربین" }
    ]
  },
  music: {
    label: "موسیقی",
    fields: ["music"],
    inputFields: [
      { key: "musicTools", label: "ابزارهای موسیقی" },
      { key: "musicDesc", label: "توضیحات موسیقی" }
    ]
  },
  karikator: {
    label: "کاریکاتور",
    fields: ["karikator"],
    inputFields: [
      { key: "karikatorDesc", label: "توضیحات کاریکاتور" }
    ]
  },
  dastan: {
    label: "داستان‌نویسی",
    fields: ["dastan"],
    inputFields: [
      { key: "dastanMethod", label: "روش داستان‌نویسی" }
    ]
  },
  sher: {
    label: "شعر",
    fields: ["sher"],
    inputFields: [
      { key: "sherDesc", label: "توضیحات شعر" }
    ]
  },
  shaer: {
    label: "شاعر",
    fields: ["shaer"],
    inputFields: [
      { key: "shaerDesc", label: "توضیحات شاعر" }
    ]
  },
  moshaere: {
    label: "مشاعره",
    fields: ["moshaere"],
    inputFields: [
      { key: "moshaereDesc", label: "توضیحات مشاعره" }
    ]
  },
  nashrie: {
    label: "نشریات",
    fields: ["nashrieElmi", "nashrieSiasi", "nashrieEjtemayi", "nashrieMazhabi"],
    inputFields: [
      { key: "nashrieDesc", label: "توضیحات نشریات" }
    ]
  },
  maghale: {
    label: "مقاله‌نویسی",
    fields: ["maghale"],
    inputFields: [
      { key: "maghaleDesc", label: "توضیحات مقاله‌نویسی" }
    ]
  },
  weblog: {
    label: "وبلاگ",
    fields: ["weblog"],
    inputFields: [
      { key: "weblogAddress", label: "آدرس وبلاگ" },
      { key: "weblogDesc", label: "توضیحات وبلاگ" }
    ]
  },
  khabgah: {
    label: "فعالیت‌های خوابگاه",
    fields: ["khabgahTarbiatBadani", "khabgahKetabkhane", "khabgahTaghzie", "khabgahTablighat", "khabgahTasisat", "khabgahModiriat", "khabgahOrduyi", "khabgahSite"]
  },
  anjoman: {
    label: "فعالیت‌های اجتماعی",
    fields: ["anjomanEslami", "basij", "mazhabi", "farhangsara", "masjed", "shoraMadrese", "basijMahale", "moaseseFarhangi", "ngo"],
    inputFields: [
      { key: "fogheBarnameDesc", label: "توضیحات فوق برنامه" }
    ]
  }
};

export const CulturalCapabilities = () => {
  const [t] = useTranslation(['common', 'survey']);
  const { message } = App.useApp();

  const { data: culturalCapabilities, isLoading, isError } = useQuery({
    queryKey: ['/survey/cultural-capabilities'],
    queryFn: envConfig.onPremise.core.showAlternativeCulturalCapabilities ? fetchAltCulturalCapabilities : fetchCulturalCapabilities,
  });
  let electionSurveys = [];
  if (envConfig.onPremise.core.showAlternativeCulturalCapabilities && culturalCapabilities) {
    electionSurveys = culturalCapabilities?.filter(survey => survey.type === SurveyApiType.CulturalCapabilities);
  }

  const { mutate: submitCulturalCapabilitiesMutation } = useMutation({
    mutationFn: async (data: CulturalCapabilitiesEntity) => (await submitCulturalCapabilities(data)) ?? {},
    mutationKey: ['/survey/update/cultural-capabilities'],
    onSuccess: () => {
      message.success(t('common:messages.success-submit-description'));
    },
    onError: () => {
      message.error(t('common:messages.error-submit-description'));
    }
  });

  const [selectedCapabilities, setSelectedCapabilities] = useState<string[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [showForm, setShowForm] = useState(false);

  const hasExistingData = useMemo(() => {
    if (!culturalCapabilities) return false;

    const booleanFields = Object.keys(culturalCapabilities).filter(key =>
      typeof culturalCapabilities[key as keyof CulturalCapabilitiesEntity] === 'boolean'
    );

    return booleanFields.some(field =>
      culturalCapabilities[field as keyof CulturalCapabilitiesEntity] === true
    );
  }, [culturalCapabilities]);

  // Initialize form data when we have existing data
  React.useEffect(() => {
    if (hasExistingData && culturalCapabilities && Object.keys(formData).length === 0) {
      const initialFormData: Record<string, string> = {};

      Object.entries(CAPABILITY_MAPPING).forEach(([, config]) => {
        const hasActiveField = config.fields?.some(field =>
          culturalCapabilities[field as keyof CulturalCapabilitiesEntity] === true
        );

        if (hasActiveField && config.inputFields) {
          config.inputFields.forEach(inputField => {
            initialFormData[inputField.key] = (culturalCapabilities[inputField.key as keyof CulturalCapabilitiesEntity] as string) || '';
          });
        }
      });

      setFormData(initialFormData);
    }
  }, [hasExistingData, culturalCapabilities, formData]);

  const allInputFields = useMemo(() => {
    if (!hasExistingData || !culturalCapabilities) return [];

    const fields: Array<{
      key: string;
      label: string;
      value: string;
    }> = [];

    Object.entries(CAPABILITY_MAPPING).forEach(([, config]) => {
      const hasActiveField = config.fields?.some(field =>
        culturalCapabilities[field as keyof CulturalCapabilitiesEntity] === true
      );

      if (hasActiveField && config.inputFields) {
        config.inputFields.forEach(inputField => {
          fields.push({
            key: inputField.key,
            label: inputField.label,
            value: (culturalCapabilities[inputField.key as keyof CulturalCapabilitiesEntity] as string) || ''
          });
        });
      }
    });

    return fields;
  }, [hasExistingData, culturalCapabilities]);

  const handleCapabilitySelect = (capabilityKey: string, checked: boolean) => {
    setSelectedCapabilities(prev =>
      checked
        ? [...prev, capabilityKey]
        : prev.filter(key => key !== capabilityKey)
    );
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    const submissionData = buildSubmissionData([], formData, CAPABILITY_MAPPING, culturalCapabilities);
    submitCulturalCapabilitiesMutation(submissionData);
  };

  const handleSubmitSelection = () => {
    if (selectedCapabilities.length > 0) {
      const submissionData = buildSubmissionData(selectedCapabilities, formData, CAPABILITY_MAPPING);
      submitCulturalCapabilitiesMutation(submissionData);
    }
  };

  const handleNextQuestion = () => {
    setShowForm(true);
    const initialFormData: Record<string, string> = {};
    selectedCapabilities.forEach(key => {
      const config = CAPABILITY_MAPPING[key];
      if (config.inputFields) {
        config.inputFields.forEach(inputField => {
          initialFormData[inputField.key] = '';
        });
      }
    });
    setFormData(initialFormData);
  };

  if (isLoading) {
    return (
      <PageContainer title={t(`common:labels.survey-${SurveyApiType.CulturalCapabilities}`)}>
        <Spinner />
      </PageContainer>
    );
  }

  if (envConfig.onPremise.core.showAlternativeCulturalCapabilities && !isError) {
    return (
      <PageContainer title={t(`common:labels.survey-${SurveyApiType.CulturalCapabilities}`)}>
        <Flex direction="column" className="max-w-xl mx-auto">
          <SurveysList
            surveys={electionSurveys}
            onIgnore={() => { }}
            showIgnore={false}
            extraSpace
          />
        </Flex>
      </PageContainer>
    );
  }

  else if (hasExistingData && allInputFields.length > 0 && !isError) {
    return (
      <PageContainer title={t(`common:labels.survey-${SurveyApiType.CulturalCapabilities}`)}>
        <div className="space-y-6">
          <div className="bg-gray-100 rounded-md p-4 space-y-6">
            <Typography.Title level={5}>
              {t('common:labels.enter-description')}
            </Typography.Title>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {allInputFields.map(inputField => (
                <div key={inputField.key} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {inputField.label}
                  </label>
                  <TextArea
                    value={formData[inputField.key] || ''}
                    onChange={(e) => handleFormChange(inputField.key, e.target.value)}
                    rows={3}
                    placeholder={t('common:labels.enter-description-placeholder')}
                    className="w-full"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end">
            <AppButton
              appVariant="nurture"
              type="primary"
              onClick={handleSubmit}
              className="w-full sm:w-32"
            >
              {t('common:labels.confirm-submit')}
            </AppButton>
          </div>
        </div>
      </PageContainer>
    );
  }

  else if (!hasExistingData && !isError) {
    if (!showForm) {
      return (
        <PageContainer title={t(`common:labels.survey-${SurveyApiType.CulturalCapabilities}`)}>
          <div className="space-y-4 p-2 sm:p-4 bg-gray-100 rounded-md font-bold">
            <p className="text-center sm:text-right">
              {t('common:labels.select-cultural-capabilities')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(CAPABILITY_MAPPING).map(([key, config], index) => {
                const isSelected = selectedCapabilities.includes(key);
                return (
                  <div
                    key={key}
                    className={`flex items-center p-3 sm:p-4 border-2 border-solid gap-3 sm:gap-5 rounded-lg cursor-pointer transition-colors duration-200 ${isSelected
                      ? 'bg-white border-primary-food shadow-md'
                      : 'bg-white border-gray-100'
                      }`}
                    onClick={() => handleCapabilitySelect(key, !isSelected)}
                  >
                    <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-md flex items-center justify-center text-xs sm:text-sm font-semibold bg-gray-300 flex-shrink-0`}>
                      {index + 1}
                    </div>
                    <span className="text-gray-800 font-medium text-sm sm:text-base">{config.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between gap-3 mt-5">
            <AppButton
              onClick={handleNextQuestion}
              disabled={selectedCapabilities.length === 0}
              className="w-full sm:w-auto"
            >
              {t('common:labels.next-question')}
            </AppButton>

            <AppButton
              appVariant="nurture"
              type="primary"
              onClick={handleSubmitSelection}
              disabled={selectedCapabilities.length === 0}
              className="w-full sm:w-auto"
            >
              {t('common:labels.confirm-submit')}
            </AppButton>
          </div>
        </PageContainer>
      );
    } else {
      const allSelectedInputFields: Array<{
        key: string;
        label: string;
      }> = [];

      selectedCapabilities.forEach(capabilityKey => {
        const config = CAPABILITY_MAPPING[capabilityKey];
        if (config.inputFields) {
          config.inputFields.forEach(inputField => {
            allSelectedInputFields.push(inputField);
          });
        }
      });

      return (
        <PageContainer title={t(`common:labels.survey-${SurveyApiType.CulturalCapabilities}`)}>
          <div className="space-y-6">
            <div className="bg-gray-100 rounded-md p-4 space-y-6">
              <Typography.Title level={5}>
                {t('common:labels.enter-description')}
              </Typography.Title>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {allSelectedInputFields.map(inputField => (
                  <div key={inputField.key} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {inputField.label}
                    </label>
                    <TextArea
                      value={formData[inputField.key] || ''}
                      onChange={(e) => handleFormChange(inputField.key, e.target.value)}
                      rows={3}
                      placeholder={t('common:labels.enter-description-placeholder')}
                      className="w-full"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
              <AppButton
                onClick={() => setShowForm(false)}
                className="w-full sm:w-auto"
              >
                {t('common:labels.previous-question')}
              </AppButton>

              <AppButton
                appVariant="nurture"
                type="primary"
                onClick={handleSubmitSelection}
                className="w-full sm:w-32"
              >
                {t('common:labels.confirm-submit')}
              </AppButton>
            </div>
          </div>
        </PageContainer>
      );
    }
  }

  return (
    <PageContainer title={t(`common:labels.survey-${SurveyApiType.CulturalCapabilities}`)}>
      <Alert
        type="warning"
        className="flex mx-auto max-h-16 mt-2 text-center"
        message={t('common:messages.no-cultural-capabilities')}
      />
    </PageContainer>
  );
}

export default CulturalCapabilities
